import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getInitialJwt } from '@/utils/urlParams';
import { getCurrentUser } from '@/api/beatlyApi';
import {
  getTokenFromStorage,
  getUserFromStorage,
  updateAuthInStorage,
  clearAuthFromStorage,
  logout,
} from '@/utils/auth';
import { showError } from '@/utils/alert';
import { AuthContext } from '@/contexts/AuthContext';
import type { AuthUser } from '@/utils/auth';

const AUTH_ROUTES = ['/login', '/signup'];

function isAuthRoute(pathname: string): boolean {
  return AUTH_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

/**
 * Resolves token from URL (captured at init) or localStorage. Validates via GET /app/v2/user.
 * If no valid user or no token, redirects to /login unless on /login or /signup.
 */
export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'redirect'>('loading');
  const [user, setUser] = useState<AuthUser | undefined>(() => getUserFromStorage());

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const token = getInitialJwt() ?? getTokenFromStorage();

      if (isAuthRoute(pathname)) {
        if (!token) {
          setStatus('authenticated');
          return;
        }
        try {
          const user = await getCurrentUser(token);
          if (cancelled) return;
          updateAuthInStorage({ token, user });
          setUser(user);
          setStatus('redirect');
          navigate('/', { replace: true });
          return;
        } catch {
          if (cancelled) return;
          clearAuthFromStorage();
          setUser(undefined);
          setStatus('authenticated');
          return;
        }
      }

      if (!token) {
        setStatus('redirect');
        navigate('/login', { replace: true });
        return;
      }

      try {
        const user = await getCurrentUser(token);
        if (cancelled) return;
        updateAuthInStorage({ token, user });
        setUser(user);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        clearAuthFromStorage();
        setUser(undefined);
        showError('Sign-in link expired or invalid. Please try again.');
        setStatus('redirect');
        navigate('/login', { replace: true });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [pathname, navigate]);

  if (status === 'redirect') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="text-[#667085] text-[15px]">Redirecting to sign in…</div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="text-[#667085] text-[15px]">Signing you in…</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
